import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useApolloClient, FetchResult } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from '@buyside/components/presentation/Button/button';
import Modal from '@buyside/components/presentation/Modal/modal';
import RatingStars from '@buyside/components/presentation/IdeaShowPage/Rating/ratingStars';
import Skeleton, {
  SKELETON_SIZES,
  SKELETON_WIDTHS
} from '@buyside/components/presentation/Skeleton/skeleton';
import { ratingCountThreshold } from './constants';

import {
  IdeaCoreDocument,
  IdeaCoreQuery,
  IdeaCurrentUserDocument,
  IdeaCurrentUserQuery,
  RateIdeaMutation,
  useIdeaRatingQuery,
  useRateIdeaMutation
} from '@interfaces/graphql-typings';
export enum RatingTypes {
  COMMUNITY = 'community',
  CURRENT_USER = 'currentuser'
}

import styles from './ratingAreaStyles.scss';

const RatingArea: FC<{
  canRateIdea: boolean;
  ideaId: string;
  isAuthor: boolean;
  isPublic: boolean;
  forCommentForm?: boolean;
}> = ({ canRateIdea, ideaId, isAuthor, isPublic, forCommentForm = false }) => {
  const [isRatingInstructionsModalOpen, setIsRatingInstructionsModalOpen] = useState(false);
  const [selectedStar, setSelectedStar] = useState(0);

  const client = useApolloClient();

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUserDocument,
    variables: { ideaId }
  })?.currentUser;

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCoreDocument,
    variables: { ideaId }
  })?.idea;

  const { data, loading, error } = useIdeaRatingQuery({
    variables: { ideaId }
  });

  const [rateIdea, { loading: submitting, error: mutationError }] = useRateIdeaMutation({
    update(cache, { data }) {
      if (!cachedCurrentUser || !cachedIdea) return;

      cache.modify({
        id: `User:${cachedCurrentUser.id}`,
        fields: {
          ideaRating: () => data?.rateIdea?.userRating || null
        }
      });

      cache.modify({
        id: `Idea:${cachedIdea.id}`,
        fields: {
          expectedPerformanceRating: () => data?.rateIdea?.idea?.expectedPerformanceRating || null
        }
      });
    }
  });

  const renderOpenRatingInstructionsModalButton = useCallback(
    () => (
      <Button
        faIcon={faInfoCircle}
        onClick={() => setIsRatingInstructionsModalOpen(true)}
        size={BUTTON_SIZES.MICRO}
        text="How to Rate an Idea"
        type={BUTTON_TYPES.LINK}
        width={BUTTON_WIDTHS.FULL}
      />
    ),
    []
  );

  if (loading || !data?.idea)
    return (
      <div className={styles.wrapper}>
        <div className={styles.ratingItem}>
          <Skeleton
            containerHeight={30}
            containerWidth={260}
            width={SKELETON_WIDTHS.MEDIUM}
            size={SKELETON_SIZES.MEDIUM}
          />
        </div>
      </div>
    );
  if (error) throw error;

  const { expectedPerformanceRating } = data.idea;
  const { closedAt, status } = cachedIdea || {};

  const disabled = isPublic || !canRateIdea || Boolean(closedAt) || status === 'draft' || isAuthor;
  const communityRating = expectedPerformanceRating?.averageRating || 0;
  const ratingCount = expectedPerformanceRating?.userRatingCount || 0;

  return (
    <div className={styles.wrapper}>
      {!forCommentForm && (
        <div className={styles.ratingItem}>
          <div className={styles.ratingBackground}>
            <div className={styles.ratingText}>Community Rating:</div>
            <RatingStars
              type={RatingTypes.COMMUNITY}
              rating={communityRating}
              hideRating={ratingCount < ratingCountThreshold}
            />
            <div className={styles.votes}>
              {ratingCount < ratingCountThreshold ? (
                <>
                  {ratingCountThreshold - ratingCount} vote
                  {ratingCountThreshold - ratingCount === 1 ? '' : 's'} needed
                </>
              ) : (
                <>
                  {ratingCount} vote{ratingCount > 1 ? 's' : ''}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {disabled ? null : (
        <div className={styles.ratingItem}>
          <div className={classNames(styles.ratingBackground, styles.userRating)}>
            <div className={classNames(styles.ratingText, styles.userRating)}>
              {submitting && (
                <FontAwesomeIcon className={styles.submittingSpinner} icon={faSpinner} spin />
              )}
              Rate This Idea:
            </div>
            <RatingStars
              type={
                forCommentForm ? `${RatingTypes.CURRENT_USER}-comment` : RatingTypes.CURRENT_USER
              }
              rating={cachedCurrentUser?.ideaRating?.score || null}
              disabled={disabled}
              selectedStar={selectedStar}
              submitting={submitting}
              handleHover={(num: number): void => setSelectedStar(num)}
              handleReset={(): void => setSelectedStar(0)}
              handleClick={(score: number): Promise<FetchResult<RateIdeaMutation>> =>
                rateIdea({ variables: { ideaId, score: score * 2 } })
              }
            />
          </div>
          <Modal
            isOpen={isRatingInstructionsModalOpen}
            onClose={() => setIsRatingInstructionsModalOpen(false)}
            renderOpenModalButton={renderOpenRatingInstructionsModalButton}
            size="lg"
            title="Idea Rating Rubric"
          >
            <div>
              <p>
                Rate each item on a scale of 1-5 (5 being the best, 1 being the worst), and take the
                average to calculate your rating for a given idea.
              </p>
              <strong>Does the analyst offer a comprehensive understanding of:</strong>
              <ol className={styles.ol}>
                <li>Company and industry background</li>
                <li>Management and incentives</li>
                <li>Valuation and catalyst path to unlock </li>
                <li>Risk/Reward (significant upside potential; with asymmetric risk/reward) </li>
                <li>Risk awareness (permanent loss of capital risk, not volatility) </li>
                <li>Differentiated investment work/edge</li>
              </ol>
            </div>
          </Modal>
          <div className={styles.error}>{mutationError?.message}</div>
        </div>
      )}
    </div>
  );
};

export default RatingArea;
